import type { ReactNode } from 'react';
import { useContext, useEffect, useRef } from 'react';
import type { WS } from '@trinity/chat';
import { QuerySocketEvent } from '@trinity/chat';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { QuerySocketContext, SetDisplayModeContext } from '../Chat/context';

interface Props {
  children: ReactNode;
  queryIdentifier: string;
}

/**
 * Listens for `QueryClientAction` WebSocket events and dispatches them to the
 * appropriate context (SetDisplayModeContext) or navigates to the message testing page.
 *
 * Mounted inside `AgentProgressContainer` so it shares the same query socket.
 */
export const QueryClientActionContainer = ({ children, queryIdentifier }: Props): ReactNode => {
  const socket = useContext(QuerySocketContext);
  const setDisplayMode = useContext(SetDisplayModeContext);
  const navigate = useNavigate();

  const queryIdentifierRef = useRef(queryIdentifier);
  useEffect(() => {
    queryIdentifierRef.current = queryIdentifier;
  });

  const setDisplayModeRef = useRef(setDisplayMode);
  useEffect(() => {
    setDisplayModeRef.current = setDisplayMode;
  });

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  useEffect(() => {
    if (!socket) return;

    const handleClientAction = (data: WS.QueryClientAction.Data) => {
      if (data.queryIdentifier !== queryIdentifierRef.current) return;

      switch (data.action.type) {
        case 'set_display_mode':
          setDisplayModeRef.current?.(data.action.mode);
          break;
        case 'open_testing_results_panel':
          navigateRef.current(`/message-testing/${data.action.runId}`);
          break;
        default:
          break;
      }
    };

    socket.on(QuerySocketEvent.QueryClientAction, handleClientAction);

    return () => {
      socket.off(QuerySocketEvent.QueryClientAction, handleClientAction);
    };
  }, [socket]);

  return children;
};
